/**
 * What the property page does after a viewing sign-up (POST /api/appointments/{id}/sign-up), shared by
 * both tour components.
 *
 * The page decides on its own rules whether it shows the sign-up button; the server decides on its own
 * rules whether the sign-up goes through. A refusal - the viewing is full, the candidate already has a
 * viewing on that day, a completed application blocks a new one - changes nothing on the server, so
 * reloading after it showed the same button again (and a confirm link, ?action=confirm, opened its
 * window again by itself): every attempt ended in the same reload. A refusal now shows why and stays.
 *
 * Only two answers leave the page, both for the address without its query:
 * - the sign-up went through (the page then shows the candidate as signed up);
 * - 401: the session is gone (logged out, or a ban ended it); the page then shows the login button.
 * A 403 does not come from the app on this route (no role or policy middleware), so it is shown as an
 * error like any other failure, and the page stays.
 *
 * @param {{data: *}|{error: *}} answer the response of a fulfilled request, or the error of a rejected one
 * @param {Object} locales the page's locale groups (common, appointments)
 * @param {string} href the current address
 * @returns {{kind: 'signed'|'refused'|'logged_out'|'failed', text: string, url: (string|null)}}
 */
export function signUpOutcome(answer, locales, href) {
  const text = (group, key) => (locales && locales[group] && locales[group][key]) || '';

  if (answer && answer.error) {
    const response = answer.error.response;
    if (response && response.status === 401) {
      const data = response.data || {};
      return { kind: 'logged_out', text: (data.banned && data.message) || text('common', 'login_required'), url: withoutQuery(href) };
    }

    return { kind: 'failed', text: text('common', 'error'), url: null };
  }

  const data = answer ? answer.data : undefined;
  if (data === undefined || data === null || data.error === undefined) {
    return { kind: 'signed', text: text('appointments', 'signed_up_message'), url: withoutQuery(href) };
  }
  if (data.error === 'full') {
    return { kind: 'refused', text: text('appointments', 'full_message'), url: null };
  }

  return { kind: 'refused', text: String(data.error), url: null };
}

/**
 * The address without its query, the fragment kept (as the sign-up has always left the page).
 */
export function withoutQuery(href) {
  const at = href.indexOf('?');
  if (at === -1) {
    return href;
  }
  const hash = href.indexOf('#', at);

  return href.slice(0, at) + (hash === -1 ? '' : href.slice(hash));
}

/**
 * Leaves the page for the given address: a reload when it is the current one, otherwise a navigation.
 */
export function leaveFor(url, location) {
  if (url === location.href) {
    location.reload();
  } else {
    location.href = url;
  }
}
